import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { Trace } from '@/features/traceability/type/Tool';
import { useMemo } from 'react';
import type { ChartConfig } from '../../type/dynamicChart';
import { COLORS } from '../../utils/palette';
import { getMetricFormatter, getMetricUnit } from '@/utils/format';
import {
  aggregateTracesByTool,
  aggregateTracesByCategory,
  aggregateTracesByReference,
} from '../../utils/trace';
import { getMetricValue, isCategoricalXAxis } from '../../utils/helper';
import { shortToolId } from '../../utils/format';
import { getSafeAggregationLevel } from '../../utils/chartSafety';
import { computeYFromTraces } from '../../utils/aggregationUtils';
import { SmartLegend } from './SmartLegend';
import type { LegendItem } from './SmartLegend';
import { ChartEmptyState } from './ChartEmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { getMetricLabelKey } from '../../utils/chartDefaults';

type TLPieChartProps = {
  config: ChartConfig;
  traces: Trace[];
};

// Check if X-axis is a categorical dimension
export function TLPieChart({ config, traces }: TLPieChartProps) {
  const { t } = useTranslation();

  // Check if X-axis is categorical dimension
  const isCategorical = isCategoricalXAxis(config.xAxis.metric);

  // Determine aggregation level
  const aggregationLevel = getSafeAggregationLevel(
    isCategorical ? 'category' : config.aggregatedBy,
    traces,
    isCategorical
  );

  // Aggregate data based on X-axis type
  const aggregatedData = useMemo(() => {
    if (isCategorical) {
      // Aggregate traces by categorical dimension
      return aggregateTracesByCategory(
        traces,
        config.xAxis.metric as 'machine' | 'material' | 'toolHolder' | 'lubrication'
      );
    }

    if (aggregationLevel === 'reference') {
      return aggregateTracesByReference(traces);
    }

    if (aggregationLevel === 'trace') {
      return traces;
    }
    // Aggregate by tool
    return aggregateTracesByTool(traces);
  }, [traces, aggregationLevel, isCategorical, config.xAxis.metric]);

  // Transform data into pie chart format
  const pieData = useMemo(() => {
    const data = aggregatedData
      .map((item) => {
        if ('category' in item) {
          // CategoryAggregation — use computeYFromTraces with rawTraces
          const value = computeYFromTraces(item.rawTraces, config.yAxis.metric, config.yAxis.aggregation);
          if (value <= 0) return null;
          return {
            name: item.category,
            value,
            traceCount: item.traceCount,
          };
        } else if ('reference' in item && 'toolCount' in item) {
          // ReferenceAggregation — keep existing getMetricValue (not in F2 scope)
          const value = getMetricValue(item, config.yAxis.metric);
          if (value === null || value <= 0) return null;
          return {
            name: item.reference,
            value,
            traceCount: item.traceCount,
            toolCount: item.toolCount,
          };
        } else if ('rawTraces' in item) {
          // ToolAggregation — use computeYFromTraces with rawTraces
          const value = computeYFromTraces(item.rawTraces, config.yAxis.metric, config.yAxis.aggregation);
          if (value <= 0) return null;
          const name =
            aggregationLevel === 'trace'
              ? `${shortToolId(item.toolId)} - ${t('analytics.dashboard.complexCharts.tooltip.trace')}`
              : shortToolId(item.toolId);
          return {
            name,
            value,
            traceCount: 'traceCount' in item ? item.traceCount : 1,
          };
        } else {
          // Raw Trace — use getMetricValue (single trace, no aggregation needed)
          const value = getMetricValue(item, config.yAxis.metric);
          if (value === null || value <= 0) return null;
          const name =
            aggregationLevel === 'trace'
              ? `${shortToolId(item.toolId)} - ${t('analytics.dashboard.complexCharts.tooltip.trace')}`
              : shortToolId(item.toolId);
          return {
            name,
            value,
            traceCount: 'traceCount' in item ? item.traceCount : 1,
          };
        }
      })
      .filter((d): d is NonNullable<typeof d> => d !== null);

    // Sort by value descending
    return data.sort((a, b) => b.value - a.value);
  }, [aggregatedData, config.yAxis.metric, config.yAxis.aggregation, aggregationLevel, t]);

  const valueFormatter = getMetricFormatter(config.yAxis.metric);
  const unit = getMetricUnit(config.yAxis.metric);

  // Prepare legend items for SmartLegend
  const legendItems: LegendItem[] = useMemo(() => {
    return pieData.map((entry, index) => ({
      value: entry.name,
      label: entry.name,
      color: COLORS[index % COLORS.length],
    }));
  }, [pieData]);

  if (pieData.length === 0) {
    return <ChartEmptyState variant="no-data" />;
  }

  // Calculate total for percentage
  const total = pieData.reduce((sum, entry) => sum + entry.value, 0);

  // Don't show labels on pie slices if there are more than 10 items
  const showLabelsOnSlices = pieData.length <= 10;

  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={400}>
        <PieChart margin={{ top: 8, right: 16, bottom: 32, left: 52 }}>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={
              config.options.showDataLabels && showLabelsOnSlices
                ? (entry: { percent?: number }) =>
                    entry.percent ? `${(entry.percent * 100).toFixed(0)}%` : ''
                : undefined
            }
            outerRadius={120}
            fill={COLORS[0]}
            dataKey="value"
          >
            {pieData.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;
              const data = payload[0].payload;
              const percentage = ((data.value / total) * 100).toFixed(1);

              return (
                <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                  <p className="font-medium">
                    {isCategorical
                      ? t(getMetricLabelKey(config.xAxis.metric))
                      : aggregationLevel === 'reference'
                        ? t('analytics.dashboard.complexCharts.tooltip.reference')
                        : t('analytics.dashboard.complexCharts.tooltip.tool')}
                    : {data.name}
                  </p>
                  <p className="font-mono">
                    {t(getMetricLabelKey(config.yAxis.metric))}
                    : {valueFormatter(data.value)}
                    {unit && ` ${unit}`}
                  </p>
                  <p className="font-mono text-muted-foreground">
                    {t('analytics.dashboard.complexCharts.tooltip.ofTotal', {
                      percent: percentage,
                    })}
                  </p>
                  {typeof data.toolCount === 'number' && (
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.toolCount', {
                        count: data.toolCount,
                      })}
                    </p>
                  )}
                  {data.traceCount > 1 && (
                    <p className="font-mono text-muted-foreground">
                      {t('analytics.dashboard.complexCharts.tooltip.aggregatedFrom', {
                        count: data.traceCount,
                      })}
                    </p>
                  )}
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* SmartLegend */}
      <SmartLegend payload={legendItems} layout="horizontal" maxVisibleItems={10} />
    </div>
  );
}
